import { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import { Gauge, StatusBadge } from "@/components/Bits";
import api, { COUNTRY_NAMES, fmtTime } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Pause, Play, RefreshCw, Trash2, AlertTriangle } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const COUNTRIES = Object.keys(COUNTRY_NAMES).sort();

const RISK_STYLES = {
  faible: "border-emerald-200 bg-emerald-50 text-emerald-700",
  modere: "border-amber-300 bg-amber-50 text-amber-800",
  eleve: "border-red-300 bg-red-50 text-red-700",
};

export default function Instances() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ label: "", green_api_instance_id: "", api_token: "", country: "FR", api_url: "", role: "sender", countries: [], all_countries: false });
  const [scopeFor, setScopeFor] = useState(null);
  const [scope, setScope] = useState({ countries: [], all_countries: false });

  const openScope = (i) => {
    setScopeFor(i);
    setScope({
      countries: (i.covered_countries || [i.country]).filter((c) => c !== i.country),
      all_countries: !!i.all_countries,
    });
  };

  const saveScope = async () => {
    try {
      await api.patch(`/instances/${scopeFor.id}`, {
        countries: scope.countries,
        all_countries: scope.all_countries,
      });
      toast.success("Pays couverts mis à jour");
      setScopeFor(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Enregistrement impossible");
    }
  };

  const [editing, setEditing] = useState(null);
  const [caps, setCaps] = useState({ daily_cap_override: "", hourly_cap_override: "" });
  const [tokenFor, setTokenFor] = useState(null);
  const [batchBusy, setBatchBusy] = useState(false);
  const [newToken, setNewToken] = useState("");

  const saveToken = async () => {
    if (!newToken.trim()) return;
    try {
      await api.patch(`/instances/${tokenFor.id}`, { api_token: newToken.trim() });
      toast.success("Token enregistré");
      setTokenFor(null);
      setNewToken("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Enregistrement impossible");
    }
  };

  const [settingsFor, setSettingsFor] = useState(null);
  const [settingsData, setSettingsData] = useState(null);

  const openSettings = async (i) => {
    setSettingsFor(i);
    setSettingsData(null);
    try {
      const { data } = await api.get(`/instances/${i.id}/settings`);
      setSettingsData(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Réglages indisponibles");
      setSettingsFor(null);
    }
  };

  const runBatch = async (i) => {
    setBatchBusy(true);
    try {
      const { data } = await api.post("/verify/run-now", {});
      toast.success(
        `${data.done} numéro(s) vérifié(s) — ${data.valid} avec WhatsApp, ${data.invalid} sans compte`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Vérification impossible");
    } finally {
      setBatchBusy(false);
    }
  };

  const humanize = async (i) => {
    try {
      const { data } = await api.post(`/instances/${i.id}/humanize`);
      toast.success("Réglages appliqués — effectifs sous ~5 min", { description: data.note });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Réglages refusés par GreenAPI");
    }
  };

  const humanizeAll = async () => {
    const { data } = await api.post("/instances/humanize-all");
    if (data.applied.length) {
      toast.success(`Réglages appliqués à ${data.applied.length} instance(s)`);
    }
    if (data.failed.length) {
      toast.error(`Échec sur : ${data.failed.join(", ")}`);
    }
    load();
  };

  const load = async () => setItems((await api.get("/instances")).data);
  useEffect(() => { load(); const t = setInterval(load, 15000); return () => clearInterval(t); }, []);

  const create = async () => {
    try {
      await api.post("/instances", { ...form, api_url: form.api_url || null });
      toast.success("Instance ajoutée en préchauffage");
      setOpen(false);
      setForm({ label: "", green_api_instance_id: "", api_token: "", country: "FR", api_url: "" });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
  };

  const setStatus = async (inst, status) => {
    await api.patch(`/instances/${inst.id}`, { status });
    toast.success(status === "paused" ? "Instance mise en pause" : "Instance réactivée");
    load();
  };

  const check = async (inst) => {
    try {
      const { data } = await api.post(`/instances/${inst.id}/check`);
      toast[data.state === "authorized" ? "success" : "error"](`État : ${data.state}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Vérification impossible");
    }
    load();
  };

  const saveCaps = async () => {
    await api.patch(`/instances/${editing.id}`, {
      daily_cap_override: caps.daily_cap_override ? Number(caps.daily_cap_override) : null,
      hourly_cap_override: caps.hourly_cap_override ? Number(caps.hourly_cap_override) : null,
    });
    toast.success("Quotas mis à jour");
    setEditing(null);
    load();
  };

  const remove = async (inst) => {
    await api.delete(`/instances/${inst.id}`);
    toast.success("Instance supprimée");
    load();
  };

  return (
    <Layout
      title="Instances"
      subtitle="Santé, préchauffage et quotas des instances GreenAPI"
      actions={
        <div className="flex flex-wrap gap-2">
          <button onClick={humanizeAll} data-testid="humanize-all-btn"
            className="rounded-lg border border-sky-300 bg-sky-50 px-4 py-2.5 text-sm font-medium text-sky-700 hover:bg-sky-100">
            Optimiser toutes les instances
          </button>
          <button onClick={() => setOpen(true)} data-testid="add-instance-btn"
            className="rounded-lg bg-emerald-500 px-4 py-2.5 text-sm font-medium text-white hover:bg-emerald-600 hover:-translate-y-[1px] transition-transform duration-200 flex items-center gap-2">
            <Plus className="h-4 w-4" /> Ajouter
          </button>
        </div>
      }
    >
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="instances-page">
        {items.length === 0 && (
          <div className="card-tactical p-8 text-center col-span-full">
            <p className="text-sm text-gray-500">Aucune instance. Ajoutez votre première instance GreenAPI.</p>
          </div>
        )}
        {items.map((i) => (
          <div key={i.id} className="card-tactical p-5" data-testid={`instance-card-${i.id}`}>
            <div className="flex items-start justify-between">
              <div>
                <p className="font-medium text-gray-900">{i.label}</p>
                <p className="mono text-xs text-gray-500 mt-0.5">{i.green_api_instance_id}</p>
                <p className="mono text-[10px] text-gray-400">{i.api_url}</p>
                <p className="text-xs text-gray-500 mt-1">
                  {i.all_countries
                    ? "Tous les pays"
                    : (i.covered_countries || [i.country]).map((c) => COUNTRY_NAMES[c] || c).join(", ")}
                  {" · "}Jour {i.warmup_day} de préchauffage
                  {i.role === "verifier" && (
                    <span className="ml-1 rounded-full bg-sky-100 px-2 py-0.5 text-[10px] font-medium text-sky-700"
                      data-testid={`verifier-badge-${i.id}`}>Vérification</span>
                  )}
                </p>
              </div>
              <StatusBadge status={i.status} testId={`instance-status-${i.id}`} />
            </div>

            <div className="space-y-3 mt-5">
              <Gauge value={i.sent_today} max={i.daily_cap} label="Quota journalier" testId={`gauge-day-${i.id}`} />
              <Gauge value={i.sent_this_hour} max={i.hourly_cap} label="Quota horaire" testId={`gauge-hour-${i.id}`} />
            </div>

            <div className="mt-4 grid grid-cols-2 gap-3 text-xs text-gray-500">
              <p>Dernier envoi<br /><span className="mono text-gray-800">{fmtTime(i.last_send_at)}</span></p>
              <p>Erreurs 24h<br /><span className="mono text-gray-800">{i.error_count_24h || 0}</span></p>
            </div>

            {i.has_token === false && (
              <div className="mt-3 flex items-start gap-2 rounded-lg bg-red-50 border border-red-200 p-2.5"
                data-testid={`missing-token-${i.id}`}>
                <AlertTriangle className="h-3.5 w-3.5 text-red-600 mt-0.5" />
                <p className="text-xs text-red-900">
                  Token absent : ressaisissez-le avec le bouton « Token » pour relancer les envois.
                </p>
              </div>
            )}

            {i.paused_reason && (
              <div className={`mt-3 flex items-start gap-2 rounded-lg border p-2.5 ${
                i.paused_kind === "session_closed"
                  ? "bg-red-50 border-red-200" : "bg-amber-50 border-amber-200"}`}
                data-testid={`paused-reason-${i.id}`}>
                <AlertTriangle className={`h-3.5 w-3.5 mt-0.5 ${
                  i.paused_kind === "session_closed" ? "text-red-600" : "text-amber-600"}`} />
                <div className="min-w-0">
                  <p className={`text-xs ${
                    i.paused_kind === "session_closed" ? "text-red-900" : "text-amber-900"}`}>
                    {i.paused_reason}
                  </p>
                  {i.paused_kind === "token_missing" && (
                    <p className="text-[11px] text-amber-800 mt-1">
                      Problème de configuration, pas de sanction WhatsApp : ressaisissez le token.
                    </p>
                  )}
                  {i.paused_kind === "session_closed" && (
                    <p className="text-[11px] text-red-800 mt-1">
                      Sanction ou déconnexion côté WhatsApp : rescannez le QR dans la console
                      GreenAPI, puis attendez la fin de la restriction avant tout nouvel envoi.
                    </p>
                  )}
                  {i.connection_state && (
                    <p className="mono text-[10px] text-gray-500 mt-1">
                      État GreenAPI : {i.connection_state}
                    </p>
                  )}
                </div>
              </div>
            )}

            {i.error_log?.length > 0 && (
              <details className="mt-3" data-testid={`error-log-${i.id}`}>
                <summary className="text-xs text-gray-500 cursor-pointer">Journal d'erreurs ({i.error_log.length})</summary>
                <ul className="mt-2 space-y-1 max-h-32 overflow-y-auto">
                  {i.error_log.slice(-10).reverse().map((e, idx) => (
                    <li key={idx} className="text-[11px] text-gray-600">
                      <span className="mono text-gray-400">{fmtTime(e.at)}</span> — {e.message}
                    </li>
                  ))}
                </ul>
              </details>
            )}

            {i.risk && (
              <div className={`mt-4 rounded-lg border p-3 ${RISK_STYLES[i.risk.level]}`}
                data-testid={`risk-card-${i.id}`}>
                <div className="flex items-center justify-between">
                  <p className="text-xs font-medium">Risque de bannissement : {i.risk.level_label}</p>
                  <p className="mono text-sm font-semibold" data-testid={`risk-score-${i.id}`}>
                    {i.risk.score}/100
                  </p>
                </div>
                <div className="mt-2 h-1.5 rounded-full bg-white/60 overflow-hidden">
                  <div className="h-full bg-current transition-all duration-500"
                    style={{ width: `${i.risk.score}%` }} />
                </div>
                <details className="mt-2">
                  <summary className="text-[11px] cursor-pointer opacity-80">
                    Détail ({i.risk.factors.length})
                  </summary>
                  <ul className="mt-2 space-y-1.5">
                    {i.risk.factors.map((f, idx) => (
                      <li key={idx} className="text-[11px]">
                        <span className="font-medium">
                          {f.label}{f.weight ? ` (+${f.weight})` : ""}
                        </span>
                        <br /><span className="opacity-80">{f.detail}</span>
                      </li>
                    ))}
                  </ul>
                  <p className="mt-2 text-[10px] opacity-70">
                    Calculé sur votre historique local : {i.risk.stats.sent_7d} envoi(s) et{" "}
                    {i.risk.stats.replies_7d} réponse(s) sur 7 jours. Aucune requête vers WhatsApp.
                  </p>
                </details>
              </div>
            )}

            <div className="mt-5 flex flex-wrap gap-2">
              {["active", "warming"].includes(i.status) ? (
                <button onClick={() => setStatus(i, "paused")} data-testid={`pause-instance-${i.id}`}
                  className="flex items-center gap-1.5 rounded-lg border border-gray-200 px-3 py-2 text-xs hover:bg-gray-50">
                  <Pause className="h-3.5 w-3.5" /> Pause
                </button>
              ) : (
                <button onClick={() => setStatus(i, "active")} data-testid={`resume-instance-${i.id}`}
                  className="flex items-center gap-1.5 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-700 hover:bg-emerald-100">
                  <Play className="h-3.5 w-3.5" /> Reprendre
                </button>
              )}
              <button onClick={() => check(i)} data-testid={`check-instance-${i.id}`}
                className="flex items-center gap-1.5 rounded-lg border border-gray-200 px-3 py-2 text-xs hover:bg-gray-50">
                <RefreshCw className="h-3.5 w-3.5" /> Vérifier
              </button>
              <button onClick={() => { setEditing(i); setCaps({ daily_cap_override: i.daily_cap_override || "", hourly_cap_override: i.hourly_cap_override || "" }); }}
                data-testid={`edit-caps-${i.id}`}
                className="rounded-lg border border-gray-200 px-3 py-2 text-xs hover:bg-gray-50">Quotas</button>
              <button onClick={() => { setTokenFor(i); setNewToken(""); }} data-testid={`edit-token-${i.id}`}
                className={`rounded-lg border px-3 py-2 text-xs ${
                  i.has_token === false
                    ? "border-red-300 bg-red-50 text-red-700 hover:bg-red-100"
                    : "border-gray-200 hover:bg-gray-50"}`}>Token</button>
              {i.role === "verifier" && (
                <button onClick={() => runBatch(i)} disabled={batchBusy}
                  data-testid={`verify-now-${i.id}`}
                  className="rounded-lg bg-sky-600 px-3 py-2 text-xs font-medium text-white hover:bg-sky-700 disabled:opacity-40">
                  {batchBusy ? "Vérification…" : "Vérifier maintenant"}
                </button>
              )}
              <button onClick={() => openScope(i)} data-testid={`edit-scope-${i.id}`}
                className="rounded-lg border border-gray-200 px-3 py-2 text-xs hover:bg-gray-50">
                Pays
              </button>
              <button onClick={() => openSettings(i)} data-testid={`view-settings-${i.id}`}
                className="rounded-lg border border-gray-200 px-3 py-2 text-xs hover:bg-gray-50">
                Réglages
              </button>
              <button onClick={() => humanize(i)} data-testid={`humanize-instance-${i.id}`}
                className={`rounded-lg border px-3 py-2 text-xs ${
                  i.humanized_at
                    ? "border-gray-200 hover:bg-gray-50"
                    : "border-sky-300 bg-sky-50 text-sky-700 hover:bg-sky-100"}`}>
                {i.humanized_at ? "Réglages OK" : "Optimiser"}
              </button>
              <button onClick={() => remove(i)} data-testid={`delete-instance-${i.id}`}
                className="rounded-lg border border-gray-200 px-3 py-2 text-xs text-red-600 hover:bg-red-50">
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="add-instance-dialog">
          <DialogHeader><DialogTitle>Nouvelle instance GreenAPI</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <input data-testid="instance-label-input" placeholder="Libellé (ex. FR-01)" value={form.label}
              onChange={(e) => setForm({ ...form, label: e.target.value })}
              className="w-full rounded-lg border border-gray-200 px-3 py-2.5 text-sm focus:ring-2 focus:ring-emerald-500 focus:outline-none" />
            <input data-testid="instance-id-input" placeholder="ID d'instance GreenAPI" value={form.green_api_instance_id}
              onChange={(e) => setForm({ ...form, green_api_instance_id: e.target.value })}
              className="w-full rounded-lg border border-gray-200 px-3 py-2.5 text-sm mono focus:ring-2 focus:ring-emerald-500 focus:outline-none" />
            <input data-testid="instance-token-input" placeholder="Token API" type="password" value={form.api_token}
              onChange={(e) => setForm({ ...form, api_token: e.target.value })}
              className="w-full rounded-lg border border-gray-200 px-3 py-2.5 text-sm mono focus:ring-2 focus:ring-emerald-500 focus:outline-none" />
            <select data-testid="instance-country-select" value={form.country}
              onChange={(e) => setForm({ ...form, country: e.target.value })}
              className="w-full rounded-lg border border-gray-200 px-3 py-2.5 text-sm bg-white">
              {COUNTRIES.map((c) => <option key={c} value={c}>{COUNTRY_NAMES[c]}</option>)}
            </select>
            <div className="rounded-lg border border-gray-200 p-3">
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input type="checkbox" data-testid="instance-all-countries-toggle"
                  checked={form.all_countries}
                  onChange={(e) => setForm({ ...form, all_countries: e.target.checked })} />
                Autoriser <strong>tous les pays</strong>
              </label>
              {!form.all_countries && (
                <>
                  <p className="mt-3 text-xs text-gray-500">
                    Pays supplémentaires couverts par cette instance (le pays principal est
                    toujours inclus) :
                  </p>
                  <div className="mt-2 max-h-40 overflow-y-auto grid grid-cols-2 gap-1"
                    data-testid="instance-countries-picker">
                    {COUNTRIES.filter((c) => c !== form.country).map((c) => (
                      <label key={c} className="flex items-center gap-1.5 text-xs text-gray-600">
                        <input type="checkbox" data-testid={`instance-country-${c}`}
                          checked={form.countries.includes(c)}
                          onChange={(e) => setForm({
                            ...form,
                            countries: e.target.checked
                              ? [...form.countries, c]
                              : form.countries.filter((x) => x !== c),
                          })} />
                        {COUNTRY_NAMES[c] || c}
                      </label>
                    ))}
                  </div>
                </>
              )}
            </div>
            <input data-testid="instance-api-url-input" placeholder="URL d'API (optionnel, ex. https://7105.api.greenapi.com)"
              value={form.api_url} onChange={(e) => setForm({ ...form, api_url: e.target.value })}
              className="w-full rounded-lg border border-gray-200 px-3 py-2.5 text-sm mono focus:ring-2 focus:ring-emerald-500 focus:outline-none" />
            <select data-testid="instance-role-select" value={form.role}
              onChange={(e) => setForm({ ...form, role: e.target.value })}
              className="w-full rounded-lg border border-gray-200 px-3 py-2.5 text-sm bg-white">
              <option value="sender">Envoi de campagnes</option>
              <option value="verifier">Vérification des numéros (dédiée)</option>
            </select>
            <p className="text-xs text-gray-500">
              Une instance de <strong>vérification</strong> n'envoie jamais de campagne : elle sert
              uniquement à contrôler si les numéros ont WhatsApp. Si elle est sanctionnée, vos
              numéros d'envoi restent intacts.
            </p>
            <p className="text-xs text-gray-500">
              Le token est chiffré puis conservé en base : il survit aux redéploiements et n'est
              jamais lisible en clair.
            </p>
            <button onClick={create} data-testid="save-instance-btn"
              className="w-full rounded-lg bg-emerald-500 px-4 py-2.5 text-sm font-medium text-white hover:bg-emerald-600">
              Ajouter l'instance
            </button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!settingsFor} onOpenChange={(o) => !o && setSettingsFor(null)}>
        <DialogContent data-testid="settings-dialog">
          <DialogHeader>
            <DialogTitle>Réglages GreenAPI — {settingsFor?.label}</DialogTitle>
          </DialogHeader>
          {!settingsData ? (
            <p className="text-sm text-gray-500">Lecture des réglages…</p>
          ) : (
            <>
              <div className={`rounded-lg border p-3 text-xs ${
                settingsData.humanized
                  ? "border-emerald-200 bg-emerald-50 text-emerald-800"
                  : "border-amber-300 bg-amber-50 text-amber-900"}`}
                data-testid="settings-verdict">
                {settingsData.humanized
                  ? "Cette instance est correctement humanisée."
                  : "Certains réglages exposent l'instance : cliquez sur « Optimiser » pour les corriger."}
              </div>
              <ul className="mt-3 space-y-2 max-h-72 overflow-y-auto">
                {settingsData.rows.map((r) => (
                  <li key={r.key} className="flex items-start justify-between gap-3 text-xs border-b border-gray-100 pb-2"
                    data-testid={`setting-row-${r.key}`}>
                    <div className="min-w-0">
                      <p className="text-gray-900">{r.label}</p>
                      <p className="mono text-[10px] text-gray-400">{r.key}</p>
                    </div>
                    <div className="text-right shrink-0">
                      <p className={`mono font-medium ${r.ok ? "text-emerald-600" : "text-red-600"}`}>
                        {String(r.current ?? "—")}
                      </p>
                      {!r.ok && (
                        <p className="text-[10px] text-gray-500">attendu : {String(r.expected)}</p>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
              <p className="mt-3 mono text-[10px] text-gray-500 break-all">
                Webhook : {settingsData.webhook_url || "non configuré"}
              </p>
              {!settingsData.humanized && (
                <button onClick={async () => { await humanize(settingsFor); setSettingsFor(null); }}
                  data-testid="settings-apply-btn"
                  className="mt-3 w-full rounded-lg bg-sky-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-sky-700">
                  Appliquer les réglages recommandés
                </button>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!tokenFor} onOpenChange={(o) => !o && setTokenFor(null)}>
        <DialogContent data-testid="token-dialog">
          <DialogHeader><DialogTitle>Token API — {tokenFor?.label}</DialogTitle></DialogHeader>
          <p className="text-xs text-gray-500">
            Récupérez le token dans la console GreenAPI de l'instance{" "}
            <span className="mono">{tokenFor?.green_api_instance_id}</span>. Il est chiffré avant
            d'être enregistré et reste conservé après un redéploiement.
          </p>
          <input data-testid="new-token-input" type="password" placeholder="Nouveau token API"
            value={newToken} onChange={(e) => setNewToken(e.target.value)}
            className="mt-3 w-full rounded-lg border border-gray-200 px-3 py-2.5 text-sm mono focus:ring-2 focus:ring-emerald-500 focus:outline-none" />
          <button onClick={saveToken} data-testid="save-token-btn"
            className="mt-3 w-full rounded-lg bg-emerald-500 px-4 py-2.5 text-sm font-medium text-white hover:bg-emerald-600">
            Enregistrer le token
          </button>
        </DialogContent>
      </Dialog>

      <Dialog open={!!scopeFor} onOpenChange={(o) => !o && setScopeFor(null)}>
        <DialogContent data-testid="scope-dialog">
          <DialogHeader>
            <DialogTitle>Pays couverts — {scopeFor?.label}</DialogTitle>
          </DialogHeader>
          <p className="text-xs text-gray-500">
            Cette instance enverra les messages des contacts de ces pays. Son pays principal
            ({COUNTRY_NAMES[scopeFor?.country] || scopeFor?.country}) est toujours inclus.
          </p>
          <label className="mt-3 flex items-center gap-2 text-sm text-gray-700">
            <input type="checkbox" data-testid="scope-all-countries-toggle"
              checked={scope.all_countries}
              onChange={(e) => setScope({ ...scope, all_countries: e.target.checked })} />
            Autoriser <strong>tous les pays</strong>
          </label>
          {!scope.all_countries && (
            <div className="mt-3 max-h-64 overflow-y-auto grid grid-cols-2 gap-1"
              data-testid="scope-countries-picker">
              {COUNTRIES.filter((c) => c !== scopeFor?.country).map((c) => (
                <label key={c} className="flex items-center gap-1.5 text-xs text-gray-600">
                  <input type="checkbox" data-testid={`scope-country-${c}`}
                    checked={scope.countries.includes(c)}
                    onChange={(e) => setScope({
                      ...scope,
                      countries: e.target.checked
                        ? [...scope.countries, c]
                        : scope.countries.filter((x) => x !== c),
                    })} />
                  {COUNTRY_NAMES[c] || c}
                </label>
              ))}
            </div>
          )}
          <button onClick={saveScope} data-testid="save-scope-btn"
            className="mt-3 w-full rounded-lg bg-emerald-500 px-4 py-2.5 text-sm font-medium text-white hover:bg-emerald-600">
            Enregistrer
          </button>
        </DialogContent>
      </Dialog>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent data-testid="caps-dialog">
          <DialogHeader><DialogTitle>Quotas manuels — {editing?.label}</DialogTitle></DialogHeader>
          <div className="flex items-start gap-2 rounded-lg bg-amber-50 border border-amber-200 p-3">
            <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5" />
            <p className="text-xs text-amber-900">
              Forcer les quotas au-delà du calendrier de préchauffage augmente fortement le risque
              de blocage de l'instance.
            </p>
          </div>
          <div className="space-y-3 mt-3">
            <label className="block text-sm text-gray-700">Quota journalier
              <input data-testid="daily-cap-input" type="number" value={caps.daily_cap_override}
                onChange={(e) => setCaps({ ...caps, daily_cap_override: e.target.value })}
                placeholder={`Auto : ${editing?.daily_cap}`}
                className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2.5 text-sm mono" />
            </label>
            <label className="block text-sm text-gray-700">Quota horaire
              <input data-testid="hourly-cap-input" type="number" value={caps.hourly_cap_override}
                onChange={(e) => setCaps({ ...caps, hourly_cap_override: e.target.value })}
                placeholder={`Auto : ${editing?.hourly_cap}`}
                className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2.5 text-sm mono" />
            </label>
            <button onClick={saveCaps} data-testid="save-caps-btn"
              className="w-full rounded-lg bg-emerald-500 px-4 py-2.5 text-sm font-medium text-white hover:bg-emerald-600">
              Enregistrer
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
